import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateWoodboxPriceRequestBody,
  GetWoodboxPriceResponse,
  UpdateWoodboxPriceRequestBody,
} from "../types/api/api-types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";

export const getWoodboxPriceDefault = async () => {
  return axiosApi.get<GetWoodboxPriceResponse>("/wood-boxes-price").then((res) => res.data);
};

export const useGetWoodboxPriceDefault = () => {
  return useQuery({
    queryKey: ["wood-boxes-price", "default"],
    queryFn: () => getWoodboxPriceDefault(),
  });
};

export const getWoodboxPriceCompany = async () => {
  return axiosApi.get<GetWoodboxPriceResponse>("/wood-boxes-price/company").then((res) => res.data);
};

export const useGetWoodboxPriceCompany = () => {
  return useQuery({
    queryKey: ["wood-boxes-price", "company"],
    queryFn: () => getWoodboxPriceCompany(),
  });
};

export const getWoodboxPriceByCompanyId = async (companyId: string) => {
  return axiosApi
    .get<GetWoodboxPriceResponse>("/wood-boxes-price/company/" + companyId)
    .then((res) => res.data);
};

export const useGetWoodboxPriceByCompanyId = (companyId: string) => {
  return useQuery({
    queryKey: ["wood-boxes-price", "company", companyId],
    queryFn: () => getWoodboxPriceByCompanyId(companyId),
  });
};

const createWoodboxesPrice = async (data: CreateWoodboxPriceRequestBody) => {
  return axiosApi.post(`/wood-boxes-price`, data);
};

export const useCreateWoodboxesPrice = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");
  return useMutation({
    mutationFn: createWoodboxesPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["wood-boxes-price", "company"],
      });
    },
    onError: () => {
      toast.error(t("woodboxPriceApi.createError"));
    },
  });
};

const updateWoodboxesPrice = async (data: UpdateWoodboxPriceRequestBody) => {
  return axiosApi.patch(`/wood-boxes-price`, data);
};

export const useUpdateWoodboxesPrice = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");
  return useMutation({
    mutationFn: updateWoodboxesPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wood-boxes-price"] });
      queryClient.invalidateQueries({ queryKey: ["getGlobalParameters"] });
    },
    onError: () => {
      toast.error(t("woodboxPriceApi.updateError"));
    },
  });
};

const deleteWoodboxesPrice = async (companyId: string) => {
  return axiosApi.delete(`/wood-boxes-price/company/${companyId}`);
};

export const useDeleteWoodboxesPrice = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");
  return useMutation({
    mutationFn: deleteWoodboxesPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["wood-boxes-price", "company"],
      });
    },
    onError: () => {
      toast.error(t("woodboxPriceApi.deleteError"));
    },
  });
};
